// packages/chordpro/src/doc.js
//
// Текст → {meta, blocks} для чтения. Ничего не хранится: документ строится
// заново при каждом рендере, поэтому рассинхронизироваться нечему.
//
// Правило совместимости шире, чем требует спека ChordPro («unknown environments
// should be treated as part of the song lyrics»): любая незнакомая директива
// НЕ рисуется, но и не мешает читать остальное, а в тексте остаётся дословно —
// текст мы здесь не переписываем вовсе.

import { directiveOf, isSectionLine, bracketOnly } from './lines.js';
import { sectionKind } from './section-name.js';

const META_KEYS = new Set([
  'title', 'subtitle', 'artist', 'composer', 'album', 'year',
  'key', 'time', 'tempo', 'duration', 'capo', 'copyright',
]);

const COMMENT_KEYS = new Set(['comment', 'comment_italic', 'comment_box']);
const VERBATIM_ENVS = new Set(['tab', 'grid']);

/**
 * @param {string} text
 * @param {'B'|'H'} [notation]
 * @returns {{meta:Object, blocks:Array}}
 */
export function parseChartDoc(text, notation = 'B') {
  const meta = {};
  const blocks = [];
  let cur = null;
  let verbatim = false;
  let ln = -1;

  const open = (kind, name) => {
    cur = { kind, name: name || null, lines: [], tab: VERBATIM_ENVS.has(kind), line: ln };
    blocks.push(cur);
  };

  for (const raw of String(text ?? '').split('\n')) {
    ln++;

    const d = directiveOf(raw);

    // Внутри табулатуры и сетки содержимое ДОСЛОВНО, и признаём мы там только
    // парный end_of_*. Спека ChordPro требует именно этого, а практическое
    // следствие конкретное: иначе «{title: X}» внутри {sot} уехал бы в
    // метаданные песни, «{define: …}» просто исчез бы из показа, а строка,
    // начинающаяся с #, выпала бы из аппликатуры.
    if (verbatim) {
      if (d && d.name.startsWith('end_of_')) { cur = null; verbatim = false; continue; }
      if (!cur) open('other', null);
      cur.lines.push({ txt: raw, tab: true });
      continue;
    }

    if (/^\s*#/.test(raw)) continue;

    if (d) {
      const n = d.name;

      if (META_KEYS.has(n)) { meta[n] = d.value; continue; }

      if (n.startsWith('start_of_')) {
        const env = n.slice('start_of_'.length);
        // `{start_of_}` без имени — опечатка, и она РОНЯЛА разбор: `env[0]`
        // давал undefined, а `.toUpperCase()` бросал TypeError прямо на пути
        // нажатия клавиши (правка → renderSide → parseChartDoc). Список
        // разделов при этом уже был очищен, поэтому он гас насовсем. Считаем
        // такую директиву незнакомой — это ровно то, что обещает наше правило
        // совместимости.
        if (!env) continue;
        open(env, d.value || (env[0].toUpperCase() + env.slice(1)));
        verbatim = VERBATIM_ENVS.has(env);
        continue;
      }
      if (n.startsWith('end_of_')) { cur = null; continue; }

      // {chorus} — отсылка к припеву, а не второй его экземпляр. Так читают
      // с бумаги, и так на экране меньше скролла.
      if (n === 'chorus') {
        blocks.push({ kind: 'chorus', name: d.value || 'Chorus', ref: true, lines: [], line: ln });
        cur = null;
        continue;
      }

      if (COMMENT_KEYS.has(n)) {
        if (!cur) open('other', null);
        cur.lines.push({ cmt: d.value });
        continue;
      }

      continue;                                   // незнакомая — не рисуем
    }

    // Внутри табулатуры/сетки строка-раздел остаётся текстом: аппликатура
    // держится на пробелах и скобках, разбирать её нельзя.
    if (isSectionLine(raw, notation)) {
      const name = bracketOnly(raw);
      open(sectionKind(name), name);
      continue;
    }

    if (!cur) {
      if (!raw.trim()) continue;
      open('other', null);
    }
    cur.lines.push({ txt: raw, tab: false });
  }

  // Пустые строки по краям блока — артефакт разметки, внутри — дыхание автора.
  for (const b of blocks) {
    while (b.lines.length && b.lines[b.lines.length - 1].txt != null
           && !b.lines[b.lines.length - 1].txt.trim()) b.lines.pop();
    while (b.lines.length && b.lines[0].txt != null && !b.lines[0].txt.trim()) b.lines.shift();
  }

  return { meta, blocks };
}
